import {
	addBusinessDays,
	addDays,
	addMonths,
	addWeeks,
	addYears,
	differenceInDays,
	differenceInMonths,
	differenceInYears,
	endOfMonth,
	isValid,
	format,
	getYear,
	isAfter,
	isBefore,
	isSameDay,
	isWithinInterval,
	parse,
	parseISO,
	startOfMonth,
	subDays,
	subMonths,
	subWeeks,
	subYears,
} from 'date-fns';

import { isAHoliday } from '@18f/us-federal-holidays';

// -----------------------------------------------------------------------------
// Private functions
// -----------------------------------------------------------------------------

const _getPreciseAge = (dateOfBirth) => {
	const months = differenceInMonths(new Date(), dateOfBirth);
	return Math.max(months, 0) / 12;
};

const _formatDate = (date, template) => {
	return format(date, template);
};

// -----------------------------------------------------------------------------
// Custom date helpers
// -----------------------------------------------------------------------------

export const getAge = (dateOfBirth, precise = false) => {
	const dobObj = parseDate(dateOfBirth) || new Date();

	if (precise) {
		return _getPreciseAge(dobObj);
	}

	const years = differenceInYears(new Date(), dobObj);

	// normalize since it's possible to get -0
	return Math.max(years, 0);
};

export const getExactAge = (dateOfBirth) => getAge(dateOfBirth, true);

export const getFormattedDateString = (date, template = 'MMMM do, yyyy') => {
	return _formatDate(date, template);
};

export const getDateString = (date) => {
	return _formatDate(date, 'yyyy-MM-dd');
};

export function parseDate(date, format, fallbackDate) {
	if (format) {
		return parse(date, format, fallbackDate || new Date());
	}

	if (typeof date === 'string') {
		return parseISO(date);
	}

	return date;
}

export const isDateBefore = (date, dateToCompare) => {
	return isBefore(date, dateToCompare);
};

export const isDateAfter = (date, dateToCompare) => {
	return isAfter(date, dateToCompare);
};

export const isDateValid = (date) => {
	return isValid(date);
};

export const isUserBetweenCXTimeWindow = (
	utcDateMorning,
	utcDateEvening,
	currentDate
) => {
	return (
		!isAHoliday(currentDate) &&
		isDateBefore(utcDateMorning, currentDate) && // is Morning Office hours before current user's time i.e. current user's time is after 9:30
		isDateAfter(utcDateEvening, currentDate) // is Evening Office hours after current user's time i.e. 6:00pm is after current user's time
	);
};

// -----------------------------------------------------------------------------
//  date utils re-exported directly from date-fns
// -----------------------------------------------------------------------------

export {
	isSameDay,
	addWeeks,
	addMonths,
	addYears,
	addDays,
	addBusinessDays,
	subDays,
	subWeeks,
	subMonths,
	subYears,
	getYear,
	startOfMonth,
	endOfMonth,
	isWithinInterval,
	differenceInDays,
};
